import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../utils/api';
import {
  playMessageNotificationSound,
  unlockMessageNotificationSound,
  requestMessageNotificationPermission
} from '../utils/messageNotificationSound';

const POLL_MS = 30000;

function showBrowserNotification(title, body) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  try {
    const notification = new Notification(title, {
      body,
      icon: '/coown-logo-mark.svg',
      tag: 'coown-new-message',
      silent: false
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (_) { /* ignore */ }
}

function emitNewMessageAlert(count, lastAlertedCount, addToast) {
  const delta = count - lastAlertedCount;
  if (delta <= 0) return;
  const label = delta === 1 ? 'You have a new message' : `You have ${delta} new messages`;
  addToast?.(label, 'info');

  if (document.hidden) {
    if (Notification.permission === 'granted') {
      showBrowserNotification('CoOwn', label);
    } else if (Notification.permission === 'default') {
      Notification.requestPermission().then((perm) => {
        if (perm === 'granted') showBrowserNotification('CoOwn', label);
      });
    }
    return;
  }

  playMessageNotificationSound();
}

export default function useMessageNotifications({
  enabled = false,
  addToast,
  suppressAlerts = false,
}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const lastAlertedCountRef = useRef(null);
  const primedRef = useRef(false);
  const addToastRef = useRef(addToast);
  const suppressAlertsRef = useRef(suppressAlerts);

  useEffect(() => {
    addToastRef.current = addToast;
  }, [addToast]);

  useEffect(() => {
    suppressAlertsRef.current = suppressAlerts;
  }, [suppressAlerts]);

  const refreshUnreadCount = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) {
      setUnreadCount(0);
      lastAlertedCountRef.current = 0;
      return 0;
    }
    try {
      const res = await apiClient.getMessagesUnreadCount();
      const next = Number(res.data?.count) || 0;
      const lastAlerted = lastAlertedCountRef.current;

      if (!primedRef.current) {
        primedRef.current = true;
        lastAlertedCountRef.current = next;
        setUnreadCount(next);
        return next;
      }

      const hasNewUnread = lastAlerted !== null && next > lastAlerted;
      const shouldNotify = !silent && hasNewUnread && !suppressAlertsRef.current;

      if (shouldNotify) {
        emitNewMessageAlert(next, lastAlerted, addToastRef.current);
        lastAlertedCountRef.current = next;
      } else if (suppressAlertsRef.current && hasNewUnread) {
        lastAlertedCountRef.current = next;
      } else if (lastAlerted !== null && next < lastAlerted) {
        lastAlertedCountRef.current = next;
      }

      setUnreadCount(next);
      return next;
    } catch (_) {
      return lastAlertedCountRef.current ?? 0;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setUnreadCount(0);
      lastAlertedCountRef.current = 0;
      primedRef.current = false;
      return undefined;
    }

    unlockMessageNotificationSound();
    requestMessageNotificationPermission();

    refreshUnreadCount({ silent: true });
    const id = window.setInterval(() => refreshUnreadCount({ silent: false }), POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, refreshUnreadCount]);

  return { unreadCount, refreshUnreadCount };
}
